import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authApi } from '../features/auth/authApi';
import { setBootstrapped, setBootstrapWarning, clearAuth, selectIsBootstrapped } from '../features/auth/authSlice';
import { refreshSession } from './authAxios';

/**
 * Runs once, before anything renders as logged in or out — exactly the
 * sequence the API docs describe: the access token lives in memory only,
 * so a fresh page load has none, and the only way to know whether this
 * visitor has a session is to attempt POST /auth/refresh (using whatever
 * refresh token we have — see authTokens.js) and, if that succeeds, follow
 * up with GET /auth/me. A failed refresh just means "logged out", not an
 * error to surface.
 *
 * Every part of the app (Home's nav, ProtectedRoute, Topbar, ...) reads
 * the result from Redux (selectCurrentUser / selectAuthStatus) rather than
 * each firing its own GET /auth/me — this is the one place that happens.
 */
export function useAuthBootstrap() {
  const dispatch = useDispatch();
  const bootstrapped = useSelector(selectIsBootstrapped);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        const accessToken = await refreshSession();
        if (!accessToken) {
          dispatch(clearAuth());
          return;
        }
        try {
          await dispatch(authApi.endpoints.me.initiate(undefined, { forceRefetch: true })).unwrap();
        } catch (meError) {
          // authSlice's own me.matchRejected matcher has already run by
          // this point (it listens to the same dispatched action) and set
          // the correct status — including the must-change-password
          // special case. Don't clearAuth() again here, that would stomp
          // that correct state back to "unauthenticated".
          //
          // A working refresh token means a real session existed — if
          // GET /auth/me itself is the thing that failed (currently 404s
          // on the live API rather than a real 401), that's a backend gap,
          // not "you're logged out". Surface it distinctly so Login.jsx can
          // explain instead of looking like a first-time visit.
          if (meError?.status === 404) {
            dispatch(setBootstrapWarning('profile_unavailable'));
          }
        }
      } catch {
        dispatch(clearAuth());
      } finally {
        dispatch(setBootstrapped());
      }
    })();
  }, [dispatch]);

  return bootstrapped;
}

export default useAuthBootstrap;
